/**
 * @fileoverview Input/output schemas for character-snapshotter-v2 composite.
 *
 * This composite orchestrates entity detection followed by parallel
 * per-character snapshot generation.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterAttributesSchema } from '@/domains/characters/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * A character context entry for fan-out snapshot generation.
 * Each entry contains the character's ID, name, and the two context objects
 * passed to the single snapshotter: initial state (start-of-story baseline)
 * and current state (running state going into this chapter).
 */
export const CharacterContextEntrySchema = z.object({
  id: z.string().describe('The character ID'),
  name: z.string().describe('The character name'),
  initialContext: z.any().describe('The activeCharacterContext (initial / start-of-story snapshot) for this character'),
  context: z.any().describe('The activeCharacterCurrentContext (latest snapshot before this chapter) for this character'),
});

/**
 * Full input for the composite agent flow.
 *
 * Includes everything needed by both child agents:
 * - Entity detector: chapterContent, knownCharacters
 * - Single snapshotter (×N): storyContext, chapterContent, activeCharacterContext, activeCharacterCurrentContext
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.knownCharacters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  // All character contexts for fan-out
  allCharacterContexts: z.array(CharacterContextEntrySchema).describe(
    'Full context for each character in the project, used to fan out snapshot generation for detected characters.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Per-character snapshot result.
 */
export const SnapshotResultSchema = z.object({
  characterId: z.string(),
  characterName: z.string(),
  stateSnapshot: CharacterAttributesSchema,
  majorCharacterEvents: z.array(z.object({
    id: z.string(),
    description: z.string(),
  })).optional(),
  durationMs: z.number().optional(),
});

/**
 * Composite output containing detection results and all snapshots.
 */
export const OutputSchema = z.object({
  /** Character names detected as present in the chapter */
  detectedCharacterNames: z.array(z.string()),
  /** Snapshots generated for present characters */
  snapshots: z.array(SnapshotResultSchema),
  /** Number of successful snapshot generations */
  successCount: z.number(),
  /** Character names that failed snapshot generation */
  failedCharacters: z.array(z.string()).optional(),
  /** Total execution time in milliseconds */
  totalDurationMs: z.number(),
});

export type Output = z.infer<typeof OutputSchema>;
export type SnapshotResult = z.infer<typeof SnapshotResultSchema>;
